import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Coins, Check, Loader2, Zap, Crown } from "lucide-react";

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const pollStatus = useCallback(
    async (sessionId, attempt = 0) => {
      if (attempt >= 6) {
        setVerifying(false);
        toast.error("Payment status check timed out", { description: "If charged, your account updates shortly." });
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setVerifying(false);
          await refreshUser();
          toast.success("Payment successful!", {
            description: data.kind === "subscription" ? `You're now on the ${data.plan} plan.` : `You now have ${data.credits} credits.`,
          });
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          setVerifying(false);
          toast.error("Payment session expired. Please try again.");
          setParams({}, { replace: true });
          return;
        }
      } catch (e) {}
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    },
    [refreshUser, setParams]
  );

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) {
      setVerifying(true);
      pollStatus(sessionId);
    }
  }, [params, pollStatus]);

  const subscribe = async (planId) => {
    setBusy(`plan-${planId}`);
    try {
      const { data } = await api.post("/payments/subscribe", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout", { description: e.response?.data?.detail || e.message });
      setBusy("");
    }
  };

  const buyCredits = async (pkgId) => {
    setBusy(`pack-${pkgId}`);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout", { description: e.response?.data?.detail || e.message });
      setBusy("");
    }
  };

  const currentPlan = user?.plan || "free";
  const planFeatures = {
    starter: ["25 report unlocks / month", "1 WordPress site", "PDF exports & AI fixes"],
    pro: ["100 report unlocks / month", "Unlimited WordPress sites", "PDF, AI fixes & one-click push"],
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Plans & Billing</h1>
          <p className="text-gray-500 mt-2">Subscribe for monthly unlocks, or top up with pay-as-you-go credits.</p>
        </div>
        <div className="flex items-center gap-2 bg-klein/10 text-klein font-bold px-4 py-2 rounded-xl" data-testid="billing-balance">
          <Coins className="w-5 h-5" />
          <span className="font-heading text-2xl">{user?.credits ?? 0}</span>
          <span className="text-sm font-medium text-klein/70">{(user?.credits ?? 0) === 1 ? "credit" : "credits"}</span>
        </div>
      </div>

      {verifying && (
        <div className="mb-6 flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4" data-testid="billing-verifying">
          <Loader2 className="w-5 h-5 animate-spin text-klein" />
          <span className="text-sm font-medium">Verifying your payment…</span>
        </div>
      )}

      {user?.plan_active && (
        <div className="mb-8 flex items-center gap-3 bg-gray-900 text-white rounded-xl p-4" data-testid="current-plan-banner">
          <Crown className="w-5 h-5 text-yellow-400" />
          <span className="text-sm">
            You're on the <b className="capitalize">{currentPlan}</b> plan — used{" "}
            <b>{user.plan_unlocks_used}</b>{user.plan_unlocks_limit === -1 ? "" : ` / ${user.plan_unlocks_limit}`} unlocks this cycle.
          </span>
        </div>
      )}

      {/* Subscription plans */}
      <h2 className="uppercase-label mb-4">Subscription Plans</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {plans.map((p) => {
          const featured = p.id === "pro";
          const isCurrent = currentPlan === p.id && user?.plan_active;
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative bg-white rounded-2xl p-6 border shadow-sm flex flex-col ${featured ? "border-klein ring-2 ring-klein/20" : "border-gray-200"}`}
            >
              {featured && (
                <span className="absolute -top-3 left-6 z-10 bg-klein text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-sm">
                  <Zap className="w-3 h-3" /> Most popular
                </span>
              )}
              <h3 className="font-heading text-xl font-bold">{p.label}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-heading text-4xl font-black tracking-tighter">${p.amount.toFixed(0)}</span>
                <span className="text-gray-400 mb-1.5 text-sm">/ month</span>
              </div>
              <div className="mt-4 space-y-2 text-sm text-gray-600 flex-1">
                {(planFeatures[p.id] || []).map((f, i) => (
                  <div key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-good" /> {f}</div>
                ))}
              </div>
              <Button
                onClick={() => subscribe(p.id)}
                disabled={!!busy || isCurrent}
                data-testid={`subscribe-${p.id}`}
                className={`mt-6 w-full py-6 ${featured ? "bg-klein hover:bg-blue-800" : "bg-[#0A0A0A] hover:bg-gray-800"}`}
              >
                {busy === `plan-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Current plan" : `Subscribe — $${p.amount.toFixed(0)}/mo`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Credit top-ups */}
      <h2 className="uppercase-label mt-12 mb-4">Pay-as-you-go Credits</h2>
      <p className="text-sm text-gray-500 mb-4">1 credit = 1 report unlock. Great for occasional use or topping up your plan.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {packages.map((p) => (
          <div key={p.id} data-testid={`package-${p.id}`} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex flex-col">
            <h3 className="font-heading text-lg font-bold">{p.label}</h3>
            <div className="mt-2 flex items-end gap-1">
              <span className="font-heading text-3xl font-black tracking-tighter">${p.amount.toFixed(0)}</span>
            </div>
            <Button
              onClick={() => buyCredits(p.id)}
              disabled={!!busy}
              data-testid={`buy-${p.id}`}
              className="mt-5 w-full bg-[#0A0A0A] hover:bg-gray-800"
            >
              {busy === `pack-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy ${p.credits} credits`}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 mt-8 text-center">
        Secure checkout via Stripe. Test mode — card 4242 4242 4242 4242, any future expiry & CVC. Plans grant 30 days of access.
      </p>
    </div>
  );
}
